import { useEffect, useState } from "react";
import Text from "@/lib/components/Text";
import { useTranslation } from "@/lib/i18n/client";
import { LanguageParams } from "@/lib/types/PageProps";
import { faAngleDown, faArrowUpArrowDown } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Menu } from "@headlessui/react";
import Modal from "@/lib/components/Modal";
import ModalHeader from "@/lib/components/Modal/ModalHeader";
import { SortType } from "@/lib/types/Search";

export type SortByFilterProps = {
    onChange: (sortBy: SortType) => void;
} & LanguageParams;

const SortByFilter = ({ onChange, lang }: SortByFilterProps) => {
    const { t } = useTranslation(lang, "search");
    const [sortBy, setSortBy] = useState<SortType>("price_asc");

    const selectSortBy = (sort: SortType) => {
        const urlParams = new URLSearchParams(window.location.search);
        const sortOrder = urlParams.get("sortOrder");
        if (sortOrder && sortOrder !== sort) {
            urlParams.delete("sortOrder");
            const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
            window.history.replaceState({}, "", newUrl);
        }
        setSortBy(sort);
        onChange(sort);
        setIsMobileModalOpen(false);
    };

    useEffect(() => {
        if (typeof window !== "undefined") {
            const urlParams = new URLSearchParams(window.location.search);
            const sortOrder = urlParams.get("sortOrder");
            if (sortOrder && ["default", "price_asc", "price_desc", "popular"].includes(sortOrder)) {
                selectSortBy(sortOrder as SortType);
            }
        }
    });

    const [isMobileModalOpen, setIsMobileModalOpen] = useState<boolean>(false);

    const allSortTypes: SortType[] = ["default", "price_asc", "price_desc", "popular"];

    const openModal = () => {
        setIsMobileModalOpen(true);
    };
    const closeModal = () => {
        setIsMobileModalOpen(false);
    };

    return (
        <div className="flex flex-row  px-2 gap-3 justify-between">
            <div className="relative flex items-center">
                <div className="md:hidden inline-block cursor-pointer flex" onClick={openModal}>
                    <FontAwesomeIcon onClick={openModal} icon={faArrowUpArrowDown} />
                    <Text as="span" className="font-bold">
                        {t("sort-by.title").slice(0, -1)}
                    </Text>
                </div>
                <div className="md:inline-block hidden">
                    <FontAwesomeIcon icon={faArrowUpArrowDown} />
                    <Text as="span" className="font-bold mr-1">
                        {t("sort-by.title")}
                    </Text>
                    <Menu>
                        <Menu.Button>
                            {t(`sort-by.${sortBy}`)} <FontAwesomeIcon icon={faAngleDown} />
                        </Menu.Button>
                        <Menu.Items className="flex flex-col items-start bg-white rounded-md absolute z-30  overflow-hidden grow w-full mt-1">
                            {allSortTypes.map((sortStr) => (
                                <Menu.Item key={sortStr}>
                                    {({ active }) => (
                                        <button
                                            className={`px-5 py-1 w-full text-left ${active && "bg-cyan-500"}`}
                                            onClick={() => selectSortBy(sortStr)}
                                        >
                                            {t(`sort-by.${sortStr}`)}
                                        </button>
                                    )}
                                </Menu.Item>
                            ))}
                        </Menu.Items>
                    </Menu>
                </div>
                <Modal open={isMobileModalOpen} setOpen={() => null} mode="full-screen" onClose={closeModal}>
                    <div className="flex flex-col h-full">
                        <ModalHeader className="p-5" title="Sort" onClose={closeModal} />
                        <div className="flex grow flex-col">
                            {allSortTypes.map((sortStr, i) => (
                                <div className={`w-full ${sortStr === sortBy && "bg-cyan-500"}`} key={i}>
                                    <button
                                        className={`px-5 py-3 w-full text-left`}
                                        onClick={() => selectSortBy(sortStr)}
                                    >
                                        {t(`sort-by.${sortStr}`)}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </Modal>
            </div>
        </div>
    );
};

export default SortByFilter;
